// Per-widget key/value storage (the `momentum:storage` bridge call).
//
// A sandboxed srcDoc frame has an opaque origin, so the widget's own localStorage throws.
// The host keeps a small JSON object per (user, widget) in the host's localStorage under a
// namespaced key, so one widget can never read another's data — or anything else the app
// stores. Values must be JSON-serialisable; the whole store is capped so a widget cannot
// fill the browser's quota for everyone.

export const WIDGET_STORAGE_QUOTA_BYTES = 64 * 1024;
export const WIDGET_STORAGE_MAX_KEYS = 100;
export const WIDGET_STORAGE_MAX_KEY_LENGTH = 128;

const STORAGE_PREFIX = 'wg_store:';

export type StorageOp = 'get' | 'set' | 'remove' | 'keys' | 'clear' | 'getAll';

export interface WidgetStorageRequest {
  id: string;
  op: StorageOp;
  key?: unknown;
  value?: unknown;
}

export interface WidgetStorageResult {
  id: string;
  ok: boolean;
  value?: unknown;
  error?: string;
}

type StoreObject = Record<string, unknown>;

export interface StorageBackend {
  read(): StoreObject;
  write(next: StoreObject): void;
}

/** Namespacing key for the signed-in user (id, else email, else a shared bucket) so two
 *  people sharing a browser never see each other's widget data or layouts. */
export function currentUserKey(): string {
  try {
    return localStorage.getItem('userId') || localStorage.getItem('userEmail') || 'anon';
  } catch {
    return 'anon';
  }
}

/** Persistent backend: one localStorage entry per user + widget. */
export function localStorageBackend(userKey: string, toolId: string): StorageBackend {
  const storageKey = `${STORAGE_PREFIX}${userKey}:${toolId}`;
  return {
    read() {
      try {
        const raw = localStorage.getItem(storageKey);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as StoreObject) : {};
      } catch {
        return {};
      }
    },
    write(next) {
      if (Object.keys(next).length === 0) localStorage.removeItem(storageKey);
      else localStorage.setItem(storageKey, JSON.stringify(next));
    },
  };
}

/** Volatile backend for pre-save previews (no tool id yet): lives as long as the frame. */
export function memoryBackend(holder: { current: StoreObject }): StorageBackend {
  return {
    read: () => holder.current,
    write: next => { holder.current = next; },
  };
}

/** Remove a widget's stored data for this user — used on uninstall. */
export function clearWidgetStorage(userKey: string, toolId: string): void {
  try { localStorage.removeItem(`${STORAGE_PREFIX}${userKey}:${toolId}`); } catch { /* ignore */ }
}

function byteLength(text: string): number {
  return typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(text).length : text.length;
}

function normalizeKey(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const key = raw.trim();
  if (!key || key.length > WIDGET_STORAGE_MAX_KEY_LENGTH) return null;
  return key;
}

/** Applies one storage request. Never throws — problems come back as `{ ok: false, error }`. */
export function handleStorageRequest(backend: StorageBackend, request: WidgetStorageRequest): WidgetStorageResult {
  const id = String(request?.id ?? '');
  const fail = (error: string): WidgetStorageResult => ({ id, ok: false, error });
  const ok = (value?: unknown): WidgetStorageResult => ({ id, ok: true, value });

  const store = backend.read();
  switch (request?.op) {
    case 'get': {
      const key = normalizeKey(request.key);
      if (!key) return fail('Invalid storage key');
      return ok(Object.prototype.hasOwnProperty.call(store, key) ? store[key] : null);
    }
    case 'getAll':
      return ok({ ...store });
    case 'keys':
      return ok(Object.keys(store));
    case 'remove': {
      const key = normalizeKey(request.key);
      if (!key) return fail('Invalid storage key');
      if (Object.prototype.hasOwnProperty.call(store, key)) {
        const next = { ...store };
        delete next[key];
        try { backend.write(next); } catch { return fail('Storage unavailable'); }
      }
      return ok(true);
    }
    case 'clear':
      try { backend.write({}); } catch { return fail('Storage unavailable'); }
      return ok(true);
    case 'set': {
      const key = normalizeKey(request.key);
      if (!key) return fail('Invalid storage key');
      if (request.value === undefined || typeof request.value === 'function') return fail('Value must be JSON-serialisable');
      let serialisedValue: string;
      try { serialisedValue = JSON.stringify(request.value); } catch { return fail('Value must be JSON-serialisable'); }
      if (serialisedValue === undefined) return fail('Value must be JSON-serialisable');

      const next: StoreObject = { ...store, [key]: JSON.parse(serialisedValue) };
      if (Object.keys(next).length > WIDGET_STORAGE_MAX_KEYS) return fail(`Storage limit: at most ${WIDGET_STORAGE_MAX_KEYS} keys per widget`);
      const size = byteLength(JSON.stringify(next));
      if (size > WIDGET_STORAGE_QUOTA_BYTES) return fail(`Storage limit: ${Math.round(WIDGET_STORAGE_QUOTA_BYTES / 1024)} KB per widget (this write would use ${Math.ceil(size / 1024)} KB)`);
      try { backend.write(next); } catch { return fail('Storage unavailable or full'); }
      return ok(true);
    }
    default:
      return fail(`Unknown storage operation '${String(request?.op)}'`);
  }
}
